import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Bell, Shield } from 'lucide-react';
import ChangePasswordSection from './ChangePasswordSection';
import TwoFactorSection from './TwoFactorSection';

export default function SettingsTab({ user, notifPrefs, setNotifPrefs, handleSave, securityRef, privacyPrefs, setPrivacyPrefs }) {
  return (
    <>
      {/* Privacy / Contact Sharing */}
      <Card className="p-8 border border-slate-200 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-5 w-5 text-slate-700" />
          <h2 className="text-lg font-bold font-heading">Contact Sharing</h2>
        </div>
        <p className="text-sm text-slate-500 mb-6">Choose what other members can see on your profile</p>
        <div className="space-y-4">
          {[
            { key: 'show_email', label: 'Show email address', desc: 'Other members can see your email and contact you directly' },
            { key: 'show_phone', label: 'Show phone number', desc: 'Share your mobile number on your member profile' },
            { key: 'show_linkedin', label: 'Show LinkedIn profile', desc: 'Link to your LinkedIn from your member profile' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-slate-800">{label}</p>
                <p className="text-xs text-slate-400">{desc}</p>
              </div>
              <Switch data-testid={`privacy-toggle-${key}`}
                checked={privacyPrefs?.[key] ?? false}
                onCheckedChange={(checked) => setPrivacyPrefs(prev => ({ ...prev, [key]: checked }))} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-8 border border-slate-200 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="h-5 w-5 text-slate-700" />
          <h2 className="text-lg font-bold font-heading">Notification Preferences</h2>
        </div>
        <p className="text-sm text-slate-500 mb-6">Choose what you'd like to be notified about</p>
        <div className="space-y-4">
          {[
            { key: 'new_messages', label: 'Direct messages', desc: 'When someone sends you a message' },
            { key: 'meeting_requests', label: 'Meeting requests', desc: 'When another attendee asks to meet at an event — turn off to hide your "Request to Meet" button to peers' },
            { key: 'weekly_digest_emails', label: 'Weekly digest', desc: 'A round-up of community activity sent every Friday' },
            { key: 'forum_comments', label: 'Forum comments on your posts', desc: 'When someone comments on your post or replies to your comment' },
            { key: 'forum_new_posts', label: 'New posts from people you follow', desc: 'When someone you follow publishes a forum post' },
            { key: 'event_reminders', label: 'Event reminders', desc: '24-hour reminder for events you have RSVP\'d to' },
            { key: 'britelab_invites', label: 'BriteLab invitations', desc: 'When someone invites you into a BriteLab' },
            { key: 'matched_news', label: 'News matching my profile', desc: 'Articles relevant to your interests' },
            { key: 'new_events', label: 'New events announced', desc: 'When a new event is published' },
            { key: 'new_courses', label: 'New courses published', desc: 'When new learning content is available' },
            { key: 'new_members', label: 'New members join', desc: 'When someone new joins SLL' },
            { key: 'smart_match_joins', label: 'Smart match alerts', desc: 'When a new member matches your profile' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-slate-800">{label}</p>
                <p className="text-xs text-slate-400">{desc}</p>
              </div>
              <Switch data-testid={`notif-toggle-${key}`}
                checked={notifPrefs[key] ?? true}
                onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, [key]: checked }))} />
            </div>
          ))}
        </div>
      </Card>

      <Button data-testid="save-settings-button" onClick={handleSave}
        className="w-full h-12 text-base bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white mb-6">
        Save Preferences
      </Button>

      <Card className="p-6 border border-slate-200">
        <h3 className="text-sm font-bold font-heading mb-2 text-slate-500 uppercase tracking-wide">Account</h3>
        <div className="space-y-1.5 text-sm">
          <p><span className="text-slate-500">Email:</span> <span className="font-medium">{user?.email}</span></p>
          <p><span className="text-slate-500">Role:</span> <span className="font-medium capitalize">{user?.role}</span></p>
          <p><span className="text-slate-500">Member since:</span> <span className="font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span></p>
        </div>
      </Card>

      <div ref={securityRef}>
        <ChangePasswordSection />
        <TwoFactorSection />
      </div>
    </>
  );
}
